import { CreateTalentpool, privateApi, PushTalentToAtsData } from 'services';
import { CreateTalent, Talent, TalentsForListView, UpdateTalent } from './types';
import { privateApiWithRouteLeavingCancelation } from '../ApiService/ApiServiceWithRouteLeavingCancelation';

const fetchTalents = (businessAccountId: string, urlSearchParams?: URLSearchParams): Promise<Talent[]> =>
    privateApi.get<Talent[]>(
        `business-accounts/${businessAccountId}/talents${urlSearchParams?.toString() ? `?${urlSearchParams.toString()}` : ''}`,
    );

const fetchTalent = (talentId: string): Promise<Talent> => privateApi.get<Talent>(`talents/${talentId}`);

const createTalent = (businessAccountId: string, talent: CreateTalent): Promise<Talent> =>
    privateApi.post<Talent>(`business-accounts/${businessAccountId}/talents`, talent);

const updateTalentAssignee = (talentId: string, assignee: string | null): Promise<Talent> =>
    privateApi.put<Talent>(`talents/${talentId}/assignee`, { assignee });

const updateTalentStatus = (talentId: string, status: string): Promise<Talent> =>
    privateApi.put<Talent>(`talents/${talentId}/status`, { status });

const updateTalentContentTags = (talentId: string, contentTags: string[]): Promise<Talent> =>
    privateApi.put<Talent>(`talents/${talentId}/content-tags`, { contentTags });

const updateTalentRating = (talentId: string, rating: number | null): Promise<Talent> =>
    privateApi.put<Talent>(`talents/${talentId}/rating`, {
        rating,
    });

const triggerJourney = (talentId: string, flowId: string): Promise<void> =>
    privateApi.post<void>(`talents/${talentId}/trigger-journey/${flowId}`);

const cancelableTriggerJourney = (talentId: string, flowId: string, eventName: string): Promise<void> =>
    privateApiWithRouteLeavingCancelation.postWithAbortControlOnCustomEvent<void>(
        `talents/${talentId}/trigger-journey/${flowId}`,
        {},
        { customEventName: eventName },
    );

const pushTalentToAts = (talentId: string, payload: PushTalentToAtsData): Promise<void> =>
    privateApi.post<void>(`talents/${talentId}/push-to-ats`, payload);

const cancelablePushTalentToAts = (talentId: string, payload: PushTalentToAtsData, eventName: string): Promise<void> =>
    privateApiWithRouteLeavingCancelation.postWithAbortControlOnCustomEvent<void>(
        `talents/${talentId}/push-to-ats`,
        payload,
        { customEventName: eventName },
    );

const deleteTalent = (talentId: string): Promise<void> => privateApi.delete(`talents/${talentId}`);

const fetchTalentsForListView = (businessAccountId: string, urlParams?: string): Promise<TalentsForListView> =>
    privateApi.get<TalentsForListView>(
        `business-accounts/${businessAccountId}/talents-for-list-view${urlParams ? `?${urlParams}` : ''}`,
    );

const createTalentpool = (businessAccountId: string, talentpool: CreateTalentpool): Promise<void> =>
    privateApi.post<void>(`business-accounts/${businessAccountId}/leads/talentpools`, talentpool, {}, true);

const changeTalentpool = (talentId: string, data: { talentpool: string | null }): Promise<void> =>
    privateApi.put<void>(`talents/${talentId}/talentpool`, data);

const updateTalent = (talentId: string, { customFields }: UpdateTalent): Promise<Talent> =>
    privateApi.patch<Talent>(`talents/${talentId}`, { customFields });

export const TalentGateway = {
    fetchTalents,
    fetchTalent,
    createTalent,
    updateTalentAssignee,
    updateTalentContentTags,
    updateTalentStatus,
    updateTalentRating,
    triggerJourney,
    cancelableTriggerJourney,
    pushTalentToAts,
    cancelablePushTalentToAts,
    deleteTalent,
    fetchTalentsForListView,
    createTalentpool,
    changeTalentpool,
    updateTalent,
};

const cancelableFetchTalents = (businessAccountId: string, urlSearchParams?: URLSearchParams): Promise<Talent[]> =>
    privateApiWithRouteLeavingCancelation.getWithAbortControl<Talent[]>(
        `business-accounts/${businessAccountId}/talents${urlSearchParams ? `?${urlSearchParams}` : ''}`,
    );
const cancelableFetchTalent = (talentId: string): Promise<Talent> =>
    privateApiWithRouteLeavingCancelation.getWithAbortControl<Talent>(`leads/${talentId}`);

const cancelableChangeTalentpool = (
    talentId: string,
    data: { talentpool: string | null },
    settings: { customEventName: string },
): Promise<void> =>
    privateApiWithRouteLeavingCancelation.putWithAbortControlOnCustomEvent<void>(
        `talents/${talentId}/talentpool`,
        data,
        settings,
    );

export const CancelableTalentGateway = {
    cancelableFetchTalents,
    cancelableFetchTalent,
    cancelableChangeTalentpool,
};
